<template>
  <div v-if="open" class="htmljs-design-modal">
    <div class="hd">
      <input class="name" v-model="name" placeholder="Template name" spellcheck="false" />
      <div class="modes">
        <label><input type="radio" value="handlebars" v-model="bundle.mode" /> Handlebars</label>
        <label><input type="radio" value="js" v-model="bundle.mode" /> JS</label>
        <label class="starter-pick" title="Replace the editor with a starter template">
          Start from
          <select v-model="starterId" @change="loadStarter(starterId)">
            <option v-for="st in starters" :key="st.id" :value="st.id">{{ st.name }}</option>
          </select>
        </label>
      </div>
      <!-- Data source: single entry (one document per record) vs report (one document over many
           entries). Persisted on the manifest as dataSourceId "collection"; the backend /doc/data
           reads that to serve entries[] instead of a single entry, and AI generation switches to
           report-mode (tables / grouping / subtotals) when this is "Entries". -->
      <label class="datasource" title="Single entry = one document per record (invoice, receipt). Report = one document over many entries (list / grouped / totals).">
        Data
        <select v-model="dataSource" @change="fetchSample">
          <option value="Entry">Single entry</option>
          <option value="Entries">Report (many)</option>
        </select>
      </label>
      <!-- Report scope (Mode 3) + field projection live in the "Report" editor tab (shown for reports). -->
      <!-- Release-stage tag — mirrors the legacy document modal's Development field. Internal-only;
           the backend hides dev/internal-staged documents from non-admins (WorkspaceController.List
           / ActivityController.Form read development straight off the canvas manifest). -->
      <label class="stage" v-if="isInternal">
        Stage <fts-release-badge alpha class="fts-align-top" />
        <select v-model="development">
          <option :value="null">None</option>
          <option value="dev">Development</option>
          <option value="test">Testing</option>
          <option value="internal">Internal</option>
        </select>
      </label>
      <div class="actions">
        <button @click="cancel">Cancel</button>
        <button class="primary" :disabled="saving" @click="save">{{ saving ? "Saving…" : "Save" }}</button>
      </div>
    </div>

    <!-- AI teammate generation — this special teammate service surfaces ONLY here, while the
         htmljs design modal is open (the bar itself needs the generateBundle wiring to render;
         no other surface offers it). It drafts/revises the open bundle from a brief; the live
         preview below is the review surface, and nothing is stored until the designer Saves. -->
    <div class="ai-bar" v-if="generateBundle">
      <span class="ai-chip" title="AI TeamMate drafts the template; you review the live preview and Save to publish.">✦ AI</span>
      <textarea
        class="ai-brief"
        rows="1"
        v-model="ai.brief"
        :disabled="ai.busy"
        spellcheck="false"
        :placeholder="hasContent
          ? 'Describe a change (e.g. “add a remarks section and make the header navy”) — the current template is revised in place'
          : 'Describe the document (e.g. “tax invoice with company letterhead, line-item table with totals, e-invoice QR”)'"
        @keydown.enter.ctrl.exact.prevent="generate"
        @keydown.enter.meta.exact.prevent="generate"
      />
      <button
        class="ai-generate"
        :disabled="ai.busy || saving || !ai.brief.trim()"
        :title="ai.busy ? 'Generating…' : 'Generate with AI (Ctrl+Enter)'"
        @click="generate"
      >
        {{ ai.busy ? "Generating…" : hasContent ? "Revise" : "Generate" }}
      </button>
    </div>
    <div class="ai-status" v-if="generateBundle && (ai.error || ai.notes || ai.warnings || aiUsageVisible)">
      <span v-if="ai.error" class="ai-error">{{ ai.error }}</span>
      <template v-else>
        <span v-if="ai.notes" class="ai-notes">{{ ai.notes }}</span>
        <span v-if="ai.warnings" class="ai-warnings">⚠ {{ ai.warnings }}</span>
      </template>
      <!-- usage/cost readout — internal (alpha) users only, mirroring the GNey chat chips.
           The same numbers are recorded server-side per user/workspace (AITeammateUsage,
           operation "document"), so this is the visible twin of that accounting. -->
      <span v-if="aiUsageVisible" class="ai-usage">
        <span v-if="ai.usage.model" :title="`Model: ${ai.usage.model}`">🧠 {{ shortModel(ai.usage.model) }}</span>
        <span title="Input tokens (uncached)">↑ {{ formatTokens(ai.usage.inputTokens) }}</span>
        <span title="Output tokens">↓ {{ formatTokens(ai.usage.outputTokens) }}</span>
        <span v-if="ai.usage.cacheReadInputTokens" title="Prompt tokens served from cache">⚡ {{ formatTokens(ai.usage.cacheReadInputTokens) }}</span>
        <span v-if="ai.usage.latencyMs" title="Generation time">⏱ {{ formatLatency(ai.usage.latencyMs) }}</span>
        <span v-if="ai.usage.cost" :title="ai.usage.costEstimated ? 'Estimated cost (no provider-reported cost)' : 'Cost reported by the provider'">
          {{ ai.usage.costEstimated ? "≈ " : "" }}{{ formatCost(ai.usage.cost) }}
        </span>
      </span>
    </div>

    <div class="body">
      <div class="editors">
        <div class="tabs">
          <button v-for="t in tabs" :key="t" :class="{ active: tab === t }" @click="tab = t">{{ t }}</button>
        </div>
        <textarea
          v-show="tab === 'HTML'"
          v-model="bundle.html"
          class="code"
          spellcheck="false"
          :placeholder="bundle.mode === 'handlebars' ? 'Handlebars template — {{entry.field}}, {{#each entry.items}}…' : 'HTML markup'"
        />
        <textarea v-show="tab === 'CSS'" v-model="bundle.css" class="code" spellcheck="false" placeholder="CSS" />
        <textarea
          v-show="tab === 'JS'"
          v-model="bundle.js"
          class="code"
          spellcheck="false"
          placeholder="Register Handlebars partials/helpers, or inistate.onRender(...) for the imperative escape hatch"
        />

        <div v-show="tab === 'Page'" class="page-settings">
          <div class="row">
            <label>Size
              <select v-model="page.size">
                <option>A4</option><option>A5</option><option>A3</option>
                <option>Letter</option><option>Legal</option>
              </select>
            </label>
            <label>Orientation
              <select v-model="page.orientation">
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </label>
            <label>Margin
              <input v-model="page.margin" placeholder="16mm" spellcheck="false" />
            </label>
          </div>
          <label class="block">Header (running, repeats on every printed page)
            <textarea v-model="page.header" class="chrome" spellcheck="false"
              placeholder="HTML for the page header — optional" />
          </label>
          <label class="block">Footer (running, repeats on every printed page)
            <textarea v-model="page.footer" class="chrome" spellcheck="false"
              placeholder='HTML, e.g. Page <span class="pageNumber"></span> of <span class="totalPages"></span>' />
          </label>
          <p class="hint">
            Running header/footer are rendered on every page. The
            <code>.pageNumber</code>/<code>.totalPages</code> spans are filled only in the exported
            PDF (server render); the in-browser preview/print leaves them blank.
          </p>
        </div>

        <!-- Settings tab — what the legacy document designer offered around the template: the file
             name, the user-input form asked before generating, and (reports) row selection and the
             row limit. docs/documents-htmljs-legacy-parity-proposal.md (FETIAS Core). -->
        <div v-show="tab === 'Settings'" class="report-settings doc-settings">
          <div class="rs-group">
            <div class="rs-title">File name</div>
            <!-- A native formula, written in the same editor as field and automation formulas
                 (# picks Entry / Input fields) and evaluated only by the server's native engine. -->
            <fts-formula v-model="fileName" :variables="fileNameVariables" class="fn-formula" />
            <p class="hint" v-if="fileNameChecking">Checking the formula…</p>
            <p class="hint err" v-else-if="fileNameCheck.error">
              {{ fileNameCheck.error }} Until it is fixed the file is named <b>{{ fileNameCheck.fileName }}</b>.
            </p>
            <p class="hint" v-else>
              Named <b>{{ fileNameCheck.fileName || fallbackFileName }}</b> for the previewed entry — by Print,
              Download and an e-mail attachment of this document alike.
            </p>
            <p class="hint muted">
              For example CONCAT(Entry's Document ID, " - ", Entry's Customer), or TEXT(Entry's Invoice Date,
              "yyyymmdd") for a date. CONCAT, TEXT, IF, UPPER, LOWER, LEFT, RIGHT, MID and TRIM work, with the
              TODAY, NOW and CURRENTUSER constants. Empty = the document name.
            </p>
          </div>

          <div class="rs-group" v-if="dataSource === 'Entries'">
            <div class="rs-title">Rows</div>
            <label class="check">
              <input type="checkbox" v-model="allowSelection" />
              Allow selecting rows — with rows ticked in the listing, the report covers just those
            </label>
            <label>Row limit
              <input
                type="number"
                min="1"
                :max="maxRowLimit"
                v-model.number="limit"
                class="limit-input"
                placeholder="500"
              />
            </label>
            <p class="hint muted">Blank = the workspace export limit (500 by default); at most {{ maxRowLimit }}.</p>
          </div>

          <div class="rs-group" v-if="listings.length">
            <div class="rs-title">Show on listings</div>
            <div class="field-check">
              <label v-for="lst in listings" :key="lst.id" class="fc">
                <input type="checkbox" :value="lst.id" v-model="menus" /> {{ lst.name }}
              </label>
            </div>
            <p class="hint muted">None ticked = every listing. Ticked listings limit where the document is offered.</p>
          </div>

          <div class="rs-group">
            <div class="rs-title">User input</div>
            <label class="check">
              <input type="checkbox" v-model="hasUserInput" />
              Ask for input before generating
            </label>
            <p class="hint muted" v-if="hasUserInput">
              The answers bind as <code v-pre>{{inputs.[Field Name]}}</code> in the template, and as Input in
              the file-name formula.
            </p>
            <div class="user-input-designer" v-if="hasUserInput && userInput">
              <fts-form-designer :modules="workspaceModules" :form="userInput" disabled-delete class="fts-scrollbar" />
            </div>
          </div>
        </div>

        <!-- Report tab — the report's data scope (Mode 2/3) + field projection. Shown only for reports. -->
        <div v-show="tab === 'Report'" class="report-settings">
          <div class="rs-group">
            <div class="rs-title">Data scope</div>
            <label>Rows
              <select v-model="reportFixed">
                <option :value="false">Live — follows the listing &amp; filter the viewer is in</option>
                <option :value="true">Fixed — always use a specific listing</option>
              </select>
            </label>
            <label v-if="reportFixed">Listing
              <select v-model="reportListingId">
                <option :value="null">— pick a listing —</option>
                <option v-for="lst in listings" :key="lst.id" :value="lst.id">{{ lst.name }}</option>
              </select>
              <span v-if="!listings.length" class="hint">No listings found for this module.</span>
            </label>
          </div>

          <div class="rs-group">
            <div class="rs-title">Fields — columns each row carries</div>
            <label>Source
              <select v-model="projectionMode" @change="onProjectionModeChange">
                <option value="auto">Auto — from the template</option>
                <option value="all">All fields</option>
                <option value="custom">Custom — pick below</option>
              </select>
            </label>
            <p class="hint" v-if="projectionMode === 'auto'">
              Detected from your template:
              <b>{{ (resolvedProjection || []).join(", ") || "(none — the full row will be sent)" }}</b>
            </p>
            <p class="hint" v-else-if="projectionMode === 'all'">
              Every field is sent — the largest payload; use only if the report needs the whole row.
            </p>
            <div class="field-check" v-else>
              <label v-for="f in availableFields" :key="f" class="fc">
                <input type="checkbox" :value="f" v-model="customProjection" /> {{ f }}
              </label>
              <p class="hint" v-if="!availableFields.length">
                Pick a sample in the preview to load the module's field list.
              </p>
            </div>
            <p class="hint muted">
              System fields — State, Date, Created By/Date, Updated By/Date, Document Id — are always included.
            </p>
          </div>
        </div>
      </div>

      <div class="preview">
        <div class="preview-bar">
          <span>Live preview</span>
          <span v-if="previewError" class="err">{{ previewError }}</span>
          <div class="preview-actions">
            <!-- Which listing the preview samples from. "everything" ignores per-listing filters; a
                 real listing scopes the pool the way the viewer would see it. Only the first
                 PREVIEW_ENTRY_LIMIT rows are offered — this is a sample picker, not a browser. -->
            <label v-if="listings.length" class="listing-picker"
              :title="'Which listing the preview samples from — first ' + previewEntryLimit + ' entries'">
              Listing
              <select v-model="previewListingId" @change="fetchEntries">
                <option value="everything">All entries</option>
                <option v-for="lst in listings" :key="lst.id" :value="lst.id">{{ lst.name }}</option>
              </select>
            </label>
            <label v-if="entries.length" class="entry-picker">
              Entry
              <select v-model="selectedEntryId" @change="fetchSample">
                <option v-for="e in entries" :key="e.id" :value="e.id">{{ e.title }}</option>
              </select>
            </label>
            <span v-else-if="loadEntries && !entriesLoading" class="muted">no entries to render</span>
            <button class="rerender" @click="rerender"
              title="Re-render the preview now (edits normally repaint on their own after a short pause)">↻ Render</button>
            <button class="print" @click="print" title="Open the browser print dialog (Print / Save as PDF)">Print</button>
            <button class="download" @click="download"
              title="Download a PDF rendered in your browser — no server round-trip">Download</button>
            <button v-if="renderPdf" class="server-pdf" :disabled="serverRendering" @click="downloadServer"
              title="DEBUG: byte-exact PDF from the server render service (/doc/render), to compare against the client output">
              {{ serverRendering ? "Rendering…" : "Server PDF" }}
            </button>
          </div>
        </div>
        <!-- paginate="always": the design preview always shows the real page box, so Page-tab
             edits (size / orientation / margin) are visibly live even without a header/footer —
             without pagination @page is print-only CSS and those edits would appear to do nothing. -->
        <FTSHtmlJsRenderer ref="renderer" :bundle="bundle" :data="previewData" :page="page" paginate="always" height="100%" />
      </div>
    </div>
  </div>
</template>

<script>
import FTSHtmlJsRenderer from "../elements/FTSHtmlJsRenderer.vue";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSFormula from "./FTSFormula";
import { defaultHtmljsStarter, htmljsStarters } from "./htmljsStarters";
import { authorizationStore } from "../store/authorizationStore";
import canvasDesign from "../commons/canvasDesign";
import formDesign from "../commons/formDesign";

// System fields injected on every entry by /doc/data — excluded from the auto-extracted projection
// (they are always present regardless, so they never need to be listed as columns to keep).
const SYSTEM_ENTRY_FIELDS = new Set([
  "documentId", "state", "date", "createdBy", "createdDate", "updatedBy", "updatedDate",
]);

// The entry picker is a SAMPLE picker, not a browser: it offers the first N entries of the chosen
// listing. Kept in step with the page size docEntries requests (canvasDesignStore) so the dropdown
// never implies there is more to scroll to.
const PREVIEW_ENTRY_LIMIT = 50;

// The largest per-document row limit the server honours (DocumentDataService.MaxDocumentLimit, the
// legacy list branch's bound); above it the export limit applies.
const MAX_ROW_LIMIT = 20000;

export default {
  name: "FTSHtmlJsDesignModal",
  components: {
    FTSHtmlJsRenderer,
    "fts-form-designer": FTSFormDesigner,
    "fts-formula": FTSFormula,
  },
  props: {
    /** async (manifestItem, entryId) => DocumentDataModel — wire to POST /doc/data for live preview. */
    loadSampleData: { type: Function, default: null },
    /**
     * async (listingId, limit) => [{ id, title }] — wire to POST /api/workspace/list to populate the
     * entry picker. listingId is the preview-bar Listing pick ("everything" = all rows, unfiltered).
     */
    loadEntries: { type: Function, default: null },
    /** () => [{ id, name }] — the module's listings, for the fixed-report scope picker (Mode 3). */
    loadListings: { type: Function, default: null },
    /** async (bundle, page) => { ref, contentHash } — wire to POST /doc/bundle. */
    persistBundle: { type: Function, default: null },
    /** async (ref) => { bundle, page } — wire to GET /doc/bundle/{ref} to edit a saved template. */
    loadBundle: { type: Function, default: null },
    /**
     * async ({ name, brief, bundle, page }) => draft — wire to POST /api/teammate/document.
     * The AI teammate document service is exclusive to this modal: the bar only renders when this
     * prop is wired, so the capability is invisible everywhere else. The draft replaces the open
     * editor state for live-preview review; nothing persists until Save.
     */
    generateBundle: { type: Function, default: null },
    /**
     * DEBUG only: async ({ bundle, page, entryId, item, name }) => PDF Blob — wire to POST /doc/render.
     * The bar only renders the "Server PDF" button when this is provided, so the byte-exact server
     * render stays a studio-only debugging affordance; end-user downloads are client-side.
     */
    renderPdf: { type: Function, default: null },
    /**
     * async ({ formula, entryId, name, inputForm }) => { fileName, error } — wire to POST /doc/filename.
     * The file-name formula's live check: the server's native engine evaluates the UNSAVED formula
     * over the previewed entry, exactly as downloads and e-mail attachments will.
     */
    evaluateFileName: { type: Function, default: null },
  },
  data() {
    return {
      open: false,
      saving: false,
      tab: "HTML",
      name: "",
      // Release-stage tag (null | dev | test | internal); mirrors the legacy document modal's
      // `development` field. Persisted on the canvas manifest; the backend hides dev/internal
      // documents from non-admins (WorkspaceController.List.cs / ActivityController.Form.cs).
      development: null,
      // Data source: "Entry" (single doc per record) | "Entries" (report over many). Persisted on
      // the manifest (dataSourceId "collection" for Entries) and sent to AI generation for report mode.
      dataSource: "Entry",
      // Which starter the "Start from" picker last loaded.
      starterId: "invoice",
      // Report scope (Mode 3): reportFixed = pin a specific listing on the manifest, ignoring the
      // current listing the viewer is in. reportListingId = that fixed listing. listings = the pickable set.
      reportFixed: false,
      reportListingId: null,
      listings: [],
      // Report field projection (which columns each row carries). "auto" = extracted from the template
      // (shown, read-only), "all" = full row (no projection), "custom" = the author's own checklist.
      projectionMode: "auto",
      customProjection: [],
      // Settings tab (legacy parity): the file name (the manifest's `fileName`, a NATIVE formula the
      // server evaluates -- DocumentFileNameFormula), report row selection + row limit, and the
      // user-input form (`hasUserInput` + `userInput`, the legacy shape the form-footer prompt and
      // /api/activity/form already read).
      fileName: "",
      // The server's verdict on the current file-name formula for the previewed entry.
      fileNameCheck: { fileName: "", error: "" },
      fileNameChecking: false,
      fileNameTimer: null,
      fileNameSeq: 0,
      allowSelection: false,
      limit: null,
      hasUserInput: false,
      userInput: null,
      // Listing scope (`menus`, as legacy): the listings the document is offered on; none = all.
      // CanvasGuard.ComputeAuthorized(Template, listingIds) enforces it server-side.
      menus: [],
      item: null, // the canvas manifest entry being edited
      bundle: { mode: "handlebars", html: "", css: "", js: "" },
      // header/footer must exist up front so v-model stays reactive (Vue 2).
      page: { size: "A4", orientation: "portrait", margin: "16mm", header: "", footer: "" },
      sampleData: {},
      entries: [],
      // Which listing the PREVIEW samples from (entry pool + report rows). Authoring-only — never
      // saved on the manifest; the report's own scope lives in reportFixed/reportListingId.
      previewListingId: "everything",
      selectedEntryId: null,
      entriesLoading: false,
      previewError: "",
      serverRendering: false,
      resolve: null,
      saveCallBack: null,
      // AI teammate generation state (see the generateBundle prop). `usage` holds the last
      // generation's token/cost readout ({ model, inputTokens, outputTokens, cost, ... }) —
      // rendered for internal (alpha) users only.
      ai: { brief: "", busy: false, error: "", notes: "", warnings: "", usage: null },
      // Snapshot of the untouched starter bundle (set by loadStarter). While the editor still
      // equals it, generation treats the editor as EMPTY — a new document's first brief designs
      // from scratch instead of "revising" the sample invoice the starter seeds.
      starterSnapshot: "",
    };
  },
  computed: {
    /** How many entries the preview picker offers (exposed for the picker's tooltip). */
    previewEntryLimit() {
      return PREVIEW_ENTRY_LIMIT;
    },
    maxRowLimit() {
      return MAX_ROW_LIMIT;
    },
    /** The file name with no formula: the document's name. */
    fallbackFileName() {
      return (this.name || "document") + ".pdf";
    },
    /**
     * The file-name formula's scope, as the formula editor drills it: Entry (this module's fields)
     * and, when the document asks for input, Input (that form's fields) -- the roots the server's
     * DocumentFileNameFormula binds ({{entry.X}}, {{input.X}}).
     */
    fileNameVariables() {
      const state = this.$store && this.$store.state;
      const moduleId = state && state.vectorId != null ? state.vectorId.toString() : undefined;
      const roots = [{ name: "Entry", fieldName: "entry", type: 35, moduleId }];
      if (this.hasUserInput && this.userInput && Array.isArray(this.userInput.rows)) {
        roots.push({ name: "Input", fieldName: "input", type: 35, moduleId, form: this.userInput });
      }
      return roots;
    },
    /** Studio store pieces the user-input form designer needs (absent outside the studio). */
    workspaceModules() {
      const getters = this.$store && this.$store.getters;
      return (getters && getters.workspaceModules) || [];
    },
    canvasInformation() {
      const getters = this.$store && this.$store.getters;
      return (getters && getters.canvasInformation) || [];
    },
    /** Starter templates offered by the "Start from" picker. */
    starters() {
      return (Array.isArray(htmljsStarters) ? htmljsStarters : []).map((s) => ({
        id: s.id,
        name: s.name,
      }));
    },
    /** Editor tabs — the "Report" tab (scope + field projection) is shown only for reports. */
    tabs() {
      const base = ["HTML", "CSS", "JS", "Page", "Settings"];
      return this.dataSource === "Entries" ? base.concat("Report") : base;
    },
    /** The module's field display names, for the custom-projection checklist (from the loaded sample). */
    availableFields() {
      const fields =
        (this.sampleData && this.sampleData.module && this.sampleData.module.fields) || [];
      return fields.map((f) => f && f.name).filter(Boolean);
    },
    /**
     * The projection actually saved and previewed: null (full row) for "all", the author's list for
     * "custom", else the auto-extraction. A computed, so the readout tracks the template live.
     */
    resolvedProjection() {
      if (this.dataSource !== "Entries") return null;
      if (this.projectionMode === "all") return null;
      if (this.projectionMode === "custom") {
        const list = (this.customProjection || []).filter(Boolean);
        return list.length ? list : null;
      }
      return this.extractProjection();
    },
    /**
     * True once the editor holds an AUTHORED template — flips the AI bar from "Generate" to
     * "Revise" and makes generate() send the current bundle as revision context. The pristine
     * starter doesn't count (see starterSnapshot).
     */
    hasContent() {
      if (!(this.bundle && this.bundle.html && this.bundle.html.trim())) return false;
      return !this.starterSnapshot || JSON.stringify(this.bundle) !== this.starterSnapshot;
    },
    /** Alpha gate for the usage/cost chip — same flag that gates the htmljs feature itself. */
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    aiUsageVisible() {
      const u = this.ai.usage;
      return !!(this.isInternal && u && (u.inputTokens || u.outputTokens));
    },
    /**
     * Sample data for the live preview, with the selected Stage stamped onto template.development so
     * the "Testing Only" watermark (emitted by assembleHost when stage === 'test') repaints as the
     * designer toggles Stage. The design preview fetches /doc/data with documentId:"" (see
     * project_design_store_canvas_no_id), so the backend never fills development here — we do it
     * client-side. End-user viewer / server render get it from /doc/data on the real documentId.
     */
    previewData() {
      const d = this.sampleData || {};
      return Object.assign({}, d, {
        template: Object.assign({}, d.template, { development: this.development }),
      });
    },
  },
  watch: {
    // The Report tab exists only for reports; if the doc switches back to single-entry while that tab
    // is active, fall back to HTML so the editor isn't stuck on a now-hidden panel.
    dataSource(v) {
      if (v !== "Entries" && this.tab === "Report") this.tab = "HTML";
      this.scheduleFileNameCheck();
    },
    // The file-name check follows the formula, the previewed entry and the input form.
    fileName() {
      this.scheduleFileNameCheck();
    },
    selectedEntryId() {
      this.scheduleFileNameCheck();
    },
    // As the legacy designer: switching the input form on seeds an empty design to fill.
    hasUserInput(val) {
      if (val && (!this.userInput || Array.isArray(this.userInput) || !Array.isArray(this.userInput.rows))) {
        this.userInput = { rows: [{ items: [] }] };
      }
    },
  },
  methods: {
    /**
     * Mirrors the legacy modal's API: show(item, saveCallBack).
     * `item` is the canvas manifest entry ({ id, name, engine:'htmljs', templateRef, page, … }).
     * On save, saveCallBack(updatedManifest) is invoked so the console writes it into
     * canvas.documents and persists the module — exactly like the legacy document flow.
     */
    show(item, saveCallBack) {
      this.item = item || {};
      this.name = this.item.name || "New Document";
      this.development = this.item.development || null;
      this.dataSource =
        this.item.dataSource || (this.item.dataSourceId === "collection" ? "Entries" : "Entry");
      // Mode 3 (fixed report) state from the manifest.
      this.reportListingId = this.item.reportListingId || null;
      this.reportFixed = !!(this.item.reportListingId || this.item.reportFilter);
      // Projection mode + (for custom) the saved field list.
      this.projectionMode = this.item.projectionMode || "auto";
      this.customProjection =
        this.projectionMode === "custom" && Array.isArray(this.item.projection)
          ? this.item.projection.slice()
          : [];
      this.starterId = "invoice";
      this.fileName = this.item.fileName || "";
      this.fileNameCheck = { fileName: "", error: "" };
      this.allowSelection = !!this.item.allowSelection;
      this.limit = this.item.limit || null;
      this.menus = Array.isArray(this.item.menus) ? this.item.menus.slice() : [];
      this.userInput = this.item.userInput ? JSON.parse(JSON.stringify(this.item.userInput)) : null;
      if (this.userInput && Array.isArray(this.userInput.rows)) {
        canvasDesign.expandDocumentFormDesign(this.userInput, this.canvasInformation);
      }
      // After userInput: the watcher seeds an empty design only when there is none.
      this.hasUserInput = !!this.item.hasUserInput;
      this.fetchListings();
      // Preview pool: a fixed report previews its own pinned listing; everything else starts unscoped.
      this.previewListingId = (this.reportFixed && this.reportListingId) || "everything";
      this.page = this.normalizePage(this.item.page);
      this.starterSnapshot = "";
      // Editing: inline bundle > fetch saved bundle by templateRef > fall back to the starter.
      if (this.item.bundle && this.item.bundle.html) {
        this.bundle = Object.assign({ mode: "handlebars", css: "", js: "" }, this.item.bundle);
      } else {
        this.loadStarter();
        if (this.item.templateRef && this.loadBundle) this.fetchBundle();
      }
      this.saveCallBack = saveCallBack;
      this.selectedEntryId = this.item.testLocus || this.item.testLocusId || null;
      this.ai = { brief: "", busy: false, error: "", notes: "", warnings: "", usage: null };
      this.open = true;
      this.fetchEntries();
      this.scheduleFileNameCheck();
      return new Promise((res) => (this.resolve = res));
    },
    /**
     * AI teammate generation: send the brief (plus the editor's current bundle/page, so a
     * follow-up brief revises rather than restarts) and drop the returned draft into the editor.
     * The live preview repaints via the renderer's watchers — that preview is the review step;
     * the designer still Saves through the normal path to publish.
     */
    async generate() {
      const brief = this.ai.brief.trim();
      if (!this.generateBundle || this.ai.busy || !brief) return;
      this.ai.busy = true;
      this.ai.error = "";
      this.ai.notes = "";
      this.ai.warnings = "";
      this.ai.usage = null;
      try {
        const draft = (await this.generateBundle({
          name: this.name,
          brief,
          // Only send a bundle when there is one — an empty starter shouldn't read as a revision.
          bundle: this.hasContent ? this.bundle : null,
          page: this.page,
          // "Entry" | "Entries" — report mode makes the AI generate a table/grouped bundle over entries[].
          dataSource: this.dataSource,
        })) || {};

        // Token/cost readout for the alpha chip — kept even when generation fails (it still cost).
        this.ai.usage = draft.usage || null;

        const validation = draft.validation || {};
        if (draft.success === false || validation.valid === false) {
          const errors = (validation.errors || []).join("\n");
          this.ai.error = [draft.message, errors].filter(Boolean).join("\n") || "Generation failed.";
          return;
        }

        if (draft.bundle && draft.bundle.html) {
          this.bundle = Object.assign({ mode: "handlebars", html: "", css: "", js: "" }, draft.bundle);
        }
        if (draft.page) this.page = this.normalizePage(draft.page);
        // Adopt the AI's name only when the designer hasn't set a meaningful one.
        if (draft.name && (!this.name || this.name === "New Document")) this.name = draft.name;

        this.ai.notes = draft.notes || "";
        this.ai.warnings = (validation.warnings || []).join(" · ");
        // Clear the box for the next (incremental) brief — the notes line records what was done.
        this.ai.brief = "";
      } catch (e) {
        const data = e && e.response && e.response.data;
        this.ai.error =
          (data && (data.message || data.error)) || (e && e.message) || "Generation failed.";
        // eslint-disable-next-line no-console
        console.error("[htmljs] generateBundle failed", e);
      } finally {
        this.ai.busy = false;
      }
    },
    async fetchEntries() {
      this.entries = [];
      if (!this.loadEntries) {
        this.fetchSample();
        return;
      }
      this.entriesLoading = true;
      try {
        const list = (await this.loadEntries(this.previewListingId, PREVIEW_ENTRY_LIMIT)) || [];
        // Hard-cap here too: a loader that ignores the limit must not flood the picker.
        this.entries = list.slice(0, PREVIEW_ENTRY_LIMIT);
        // Default to the previously-chosen entry if it's still listed, else the first one.
        if (!this.entries.some((e) => e.id === this.selectedEntryId)) {
          this.selectedEntryId = this.entries.length ? this.entries[0].id : null;
        }
      } catch (e) {
        this.entries = [];
        const status = e && e.response && e.response.status;
        this.previewError = "could not list entries" + (status ? " (" + status + ")" : "");
        // eslint-disable-next-line no-console
        console.error("[htmljs] loadEntries failed", e);
      } finally {
        this.entriesLoading = false;
      }
      this.fetchSample();
    },
    print() {
      const r = this.$refs.renderer;
      if (r && r.print) r.print(this.name);
    },
    /** Force an immediate preview repaint (skips the debounce; also un-sticks a wedged iframe). */
    rerender() {
      const r = this.$refs.renderer;
      if (r && r.forceRender) r.forceRender();
    },
    /**
     * Client-side download: the everyday, no-server path. Uses the SAME in-browser print-to-PDF the
     * viewer uses (window.print → "Save as PDF"), seeded with the document name as the filename. The
     * exact server render (below) is reserved for backend flows / debugging, never the default download.
     */
    download() {
      const r = this.$refs.renderer;
      if (r && r.print) r.print(this.name);
    },
    /**
     * DEBUG only: fetch the byte-exact server PDF for the CURRENT edits and save it, so the designer
     * can compare the Paged.js client preview against the real render-service output. Shown only when
     * renderPdf is wired (studio-only) — end users never see it.
     */
    async downloadServer() {
      if (!this.renderPdf || this.serverRendering) return;
      this.serverRendering = true;
      this.previewError = "";
      try {
        const out = await this.renderPdf({
          bundle: this.bundle,
          page: this.page,
          entryId: this.selectedEntryId,
          item: this.item,
          name: this.name,
        });
        const blob = out instanceof Blob ? out : new Blob([out || ""], { type: "application/pdf" });
        this.saveBlob(blob, (this.name || "document") + ".pdf");
      } catch (e) {
        this.previewError = "server render failed: " + ((e && e.message) || e);
        // eslint-disable-next-line no-console
        console.error("[htmljs] server render failed", e);
      } finally {
        this.serverRendering = false;
      }
    },
    saveBlob(blob, filename) {
      if (typeof window.navigator.msSaveBlob !== "undefined") {
        window.navigator.msSaveBlob(blob, filename);
        return;
      }
      const url = (window.URL || window.webkitURL).createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () {
        (window.URL || window.webkitURL).revokeObjectURL(url);
      }, 100);
    },
    normalizePage(p) {
      return Object.assign({ size: "A4", orientation: "portrait", margin: "16mm", header: "", footer: "" }, p || {});
    },
    // Load a starter by id (Invoice / Report / Blank); defaults to the current pick or invoice.
    loadStarter(id) {
      const list = Array.isArray(htmljsStarters) ? htmljsStarters : [];
      const chosen = list.find((x) => x.id === (id || this.starterId)) || list[0];
      const s = chosen ? JSON.parse(JSON.stringify(chosen)) : defaultHtmljsStarter();
      this.starterId = s.id || "invoice";
      this.bundle = s.bundle;
      this.page = this.normalizePage(s.page);
      // A collection/report starter can declare its data source so picking it flips the modal into
      // Report mode (entries[]); single-entry starters leave the current selection alone.
      if (s.dataSource) this.dataSource = s.dataSource;
      if (!this.name || this.name === "New Document") this.name = s.name || this.name;
      // Mark the seeded starter as "not authored" so the AI bar generates fresh from the first
      // brief; any manual edit (or a generated draft) diverges from the snapshot and flips it.
      this.starterSnapshot = JSON.stringify(this.bundle);
    },
    /**
     * Auto-extract the report's projected fields (DISPLAY NAMES) from the template so /doc/data trims
     * each row to just what's rendered. Covers {{this.X}}/{{entry.X}} (bare + [bracketed]), grouping /
     * aggregate keys (by="X", sum="X", sum entries "X") and table fields ({{#each entry.[Table]}} — the
     * table field is projected, its nested columns come along). System fields are excluded (always
     * injected). Returns null when unsure (js mode / nothing found) -> the backend serves the full row.
     */
    extractProjection() {
      if (this.dataSource !== "Entries") return null;
      const b = this.bundle || {};
      if (b.mode === "js") return null;
      const text = (b.html || "") + "\n" + (b.js || "");
      const fields = new Set();
      const add = (n) => {
        n = (n || "").trim();
        if (n && !SYSTEM_ENTRY_FIELDS.has(n)) fields.add(n);
      };
      let m;
      const reBracket = /(?:this|entry)\.\[([^\]]+)\]/g;
      while ((m = reBracket.exec(text))) add(m[1]);
      const reWord = /(?:this|entry)\.([A-Za-z_][A-Za-z0-9_]*)/g;
      while ((m = reWord.exec(text))) add(m[1]);
      const reArg = /\b(?:by|sum)\s*=\s*"([^"]+)"/g;
      while ((m = reArg.exec(text))) add(m[1]);
      const reSum = /\bsum\s+[^\s"]+\s+"([^"]+)"/g;
      while ((m = reSum.exec(text))) add(m[1]);
      return fields.size ? Array.from(fields) : null;
    },
    // The module's listings for the fixed-report scope picker (Mode 3). Sync — reads the canvas.
    fetchListings() {
      this.listings = [];
      if (!this.loadListings) return;
      try {
        const list = this.loadListings() || [];
        this.listings = (Array.isArray(list) ? list : []).map((x) => ({ id: x.id, name: x.name || x.id }));
      } catch (e) {
        this.listings = [];
      }
    },
    // Switching Fields to Custom pre-fills the checklist from the auto-extraction (a sensible start).
    onProjectionModeChange() {
      if (
        this.projectionMode === "custom" &&
        (!this.customProjection || !this.customProjection.length)
      ) {
        this.customProjection = this.extractProjection() || [];
      }
    },
    async fetchBundle() {
      try {
        const r = await this.loadBundle(this.item.templateRef);
        if (r && r.bundle) {
          this.bundle = Object.assign({ mode: "handlebars", css: "", js: "" }, r.bundle);
          if (r.page) this.page = this.normalizePage(r.page);
        }
      } catch (e) {
        this.previewError = "could not load saved template";
      }
    },
    async fetchSample() {
      if (!this.loadSampleData) return;
      // A report (Entries) previews the whole list, so it needs no selected entry; a single-entry
      // document needs the chosen sample. dataSource is passed so the loader can request collection mode.
      if (this.dataSource !== "Entries" && !this.selectedEntryId) {
        this.sampleData = {};
        return;
      }
      try {
        // previewListingId scopes the report's rows to the listing being previewed (ignored for
        // single-entry documents, which render the one selected entry).
        this.sampleData =
          (await this.loadSampleData(
            this.item,
            this.selectedEntryId,
            this.dataSource,
            this.previewListingId
          )) || {};
        this.previewError = "";
      } catch (e) {
        this.previewError = "preview data unavailable";
        this.sampleData = {};
      }
    },
    scheduleFileNameCheck() {
      if (!this.evaluateFileName || !this.open) return;
      clearTimeout(this.fileNameTimer);
      this.fileNameTimer = setTimeout(() => this.checkFileName(), 400);
    },
    /** Ask the server to evaluate the current file-name formula over the previewed entry. */
    async checkFileName() {
      const formula = (this.fileName || "").trim();
      if (!this.evaluateFileName || !formula) {
        this.fileNameSeq++;
        this.fileNameChecking = false;
        this.fileNameCheck = { fileName: "", error: "" };
        return;
      }
      const seq = ++this.fileNameSeq;
      this.fileNameChecking = true;
      try {
        const result =
          (await this.evaluateFileName({
            formula,
            entryId: this.dataSource === "Entries" ? null : this.selectedEntryId,
            name: this.name,
            inputForm: this.hasUserInput ? this.userInput : null,
          })) || {};
        if (seq !== this.fileNameSeq) return;
        this.fileNameCheck = { fileName: result.fileName || "", error: result.error || "" };
      } catch (e) {
        if (seq !== this.fileNameSeq) return;
        const status = e && e.response && e.response.status;
        this.fileNameCheck = {
          fileName: this.fallbackFileName,
          error: "The formula could not be checked" + (status ? " (" + status + ")." : "."),
        };
      } finally {
        if (seq === this.fileNameSeq) this.fileNameChecking = false;
      }
    },
    /** The row limit as the server honours it (1..20000), else null = the export limit. */
    normalizedLimit() {
      const n = Number(this.limit);
      return Number.isInteger(n) && n > 0 && n <= MAX_ROW_LIMIT ? n : null;
    },
    /** The prompt is on only with a form that has at least one field (legacy formDesign.isValidForm). */
    hasUserInputValid() {
      return !!(this.hasUserInput && this.userInput && formDesign.isValidForm(this.userInput.rows));
    },
    buildManifest(ref, contentHash) {
      // The lightweight manifest stored in canvas.documents (NOT the bundle body).
      return Object.assign({}, this.item, {
        name: this.name,
        engine: "htmljs",
        engineVersion: 1,
        type: this.item.type || "PDF",
        development: this.development || null,
        // Report vs single-entry. dataSourceId "collection" is what the backend /doc/data reads to
        // serve entries[] (report) instead of one entry; keep the human-readable dataSource alongside.
        dataSource: this.dataSource,
        dataSourceId: this.dataSource === "Entries" ? "collection" : null,
        // Field projection (display names) — the backend trims report rows to these (+ system fields).
        // Auto (from template) / All (null = full row) / Custom (author's list). Null -> full row.
        projection: this.resolvedProjection,
        projectionMode: this.dataSource === "Entries" ? this.projectionMode : null,
        // Mode 3 (fixed report): pin a listing so the report ignores the current listing/filter the
        // viewer is in. Cleared when the report is dynamic (Mode 2) or single-entry.
        reportListingId:
          this.dataSource === "Entries" && this.reportFixed ? this.reportListingId : null,
        // reportFilter (ad-hoc fixed filter) is reserved for AI generation; preserve any existing value.
        reportFilter:
          this.dataSource === "Entries" && this.reportFixed ? this.item.reportFilter || null : null,
        templateRef: ref || this.item.templateRef,
        contentHash: contentHash || this.item.contentHash,
        page: this.page,
        // Settings tab. fileName: the native file-name formula (null = the document name).
        fileName: (this.fileName || "").trim() || null,
        // Report-only: honour ticked rows (`allowSelection`, legacy locusIds) and the row limit.
        allowSelection: this.dataSource === "Entries" ? !!this.allowSelection : null,
        limit: this.dataSource === "Entries" ? this.normalizedLimit() : null,
        // User input: like the legacy designer, an empty form switches the prompt off.
        hasUserInput: this.hasUserInputValid(),
        userInput: this.userInput || null,
        menus: this.menus && this.menus.length ? this.menus.slice() : null,
        // Remember the chosen sample so the next edit (and preview) defaults to it.
        testLocus: this.selectedEntryId || this.item.testLocus,
      });
    },
    async save() {
      this.saving = true;
      try {
        let ref, contentHash;
        if (this.persistBundle) {
          const r = (await this.persistBundle(this.bundle, this.page)) || {};
          ref = r.ref;
          contentHash = r.contentHash;
        }
        const manifest = this.buildManifest(ref, contentHash);
        if (this.saveCallBack) this.saveCallBack(manifest);
        if (this.resolve) this.resolve(manifest);
        this.open = false;
      } catch (e) {
        this.previewError = "save failed: " + ((e && e.message) || e);
      } finally {
        this.saving = false;
      }
    },
    cancel() {
      this.open = false;
      if (this.resolve) this.resolve(null);
    },
    /* ---- usage-chip formatting (alpha readout; mirrors the GNey chat chips) ---- */
    /** Drop the provider prefix ("anthropic/claude-opus-4.6" -> "claude-opus-4.6"). */
    shortModel(model) {
      if (!model) return "";
      const slash = String(model).lastIndexOf("/");
      return slash >= 0 ? String(model).slice(slash + 1) : String(model);
    },
    formatTokens(n) {
      const v = Number(n);
      return isFinite(v) ? v.toLocaleString() : "0";
    },
    formatLatency(ms) {
      const v = Number(ms);
      if (!isFinite(v) || v <= 0) return "";
      return v < 1000 ? `${Math.round(v)}ms` : `${(v / 1000).toFixed(1)}s`;
    },
    formatCost(usd) {
      const v = Number(usd);
      if (!isFinite(v) || v <= 0) return "$0.0000";
      return v >= 1 ? "$" + v.toFixed(2) : "$" + v.toFixed(4);
    },
  },
};
</script>

<style scoped>
/* z-index must clear the global side nav ($z-index-fixed = 1030) so the modal isn't overlapped, and stay
   below FTSModal ($z-index-modal = 1050) and popovers (1060): the Settings tab's user-input form designer
   opens its own modals (new field, formula, control) and popovers, which must land on top of this one. */
.htmljs-design-modal { position: fixed; inset: 0; background: #fff; z-index: 1045; display: flex; flex-direction: column; }
.hd { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid #e3e3e3; }
.hd .name { font-size: 16px; font-weight: 600; border: none; outline: none; flex: 0 0 240px; }
.hd .modes { display: flex; align-items: center; gap: 12px; font-size: 12px; color: #555; }
.hd .modes .starter-pick { display: flex; align-items: center; gap: 4px; font-size: 12px; }
.hd .modes .starter-pick select { font-size: 12px; padding: 2px 4px; }
.hd .stage { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #555; white-space: nowrap; }
.hd .stage select { font-size: 12px; padding: 2px 4px; }
.hd .datasource { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #555; white-space: nowrap; }
.hd .datasource select { font-size: 12px; padding: 2px 4px; }
.hd .actions { margin-left: auto; display: flex; gap: 8px; }
.hd .actions .primary { background: #0b5; color: #fff; border: none; padding: 6px 16px; border-radius: 4px; }
/* AI teammate bar — only rendered when the generateBundle service is wired (studio console). */
.ai-bar { display: flex; align-items: center; gap: 10px; padding: 8px 16px; border-bottom: 1px solid #e3e3e3; background: #fafbff; }
.ai-chip { flex-shrink: 0; font-size: 11px; font-weight: 700; color: #5b4dbe; background: #eeebff; border: 1px solid #d9d3ff; border-radius: 10px; padding: 3px 9px; cursor: default; }
.ai-brief { flex: 1; resize: none; border: 1px solid #d5d5d5; border-radius: 4px; padding: 6px 10px; font-size: 13px; line-height: 1.4; font-family: inherit; outline: none; }
.ai-brief:focus { border-color: #5b4dbe; }
.ai-generate { flex-shrink: 0; border: none; border-radius: 4px; padding: 6px 16px; background: #5b4dbe; color: #fff; font-size: 13px; cursor: pointer; }
.ai-generate:disabled { opacity: 0.55; cursor: default; }
.ai-status { padding: 5px 16px; border-bottom: 1px solid #e3e3e3; background: #fafbff; font-size: 12px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.ai-status .ai-error { color: #b00; white-space: pre-wrap; }
.ai-status .ai-notes { color: #555; }
.ai-status .ai-warnings { color: #a07828; }
/* usage/cost chip (internal alpha only) — right-aligned twin of the server-side usage rows */
.ai-status .ai-usage { margin-left: auto; display: flex; align-items: center; gap: 10px; font-size: 11px; color: #888; white-space: nowrap; }
/* Report tab — data scope + field projection, styled like the Page settings panel. */
.report-settings { flex: 1; overflow: auto; padding: 16px; display: flex; flex-direction: column; gap: 18px; font-size: 13px; color: #333; }
.report-settings .rs-group { display: flex; flex-direction: column; gap: 8px; }
.report-settings .rs-title { font-weight: 700; font-size: 12px; color: #1A5581; text-transform: uppercase; letter-spacing: .04em; }
.report-settings label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #666; max-width: 440px; }
.report-settings select { font-size: 13px; padding: 5px 8px; border: 1px solid #ccc; border-radius: 4px; }
.report-settings .hint { font-size: 11px; color: #888; margin: 2px 0 0; line-height: 1.5; }
.report-settings .hint.muted { color: #aaa; }
.report-settings .hint b { color: #444; }
.report-settings .field-check { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: 4px; }
.report-settings .field-check .fc { flex-direction: row; align-items: center; gap: 6px; font-size: 12px; color: #333; cursor: pointer; max-width: none; }
/* Settings tab — reuses the Report panel layout. */
.doc-settings label.check { flex-direction: row; align-items: center; gap: 8px; color: #333; max-width: none; }
.doc-settings .fn-formula { max-width: 640px; }
.doc-settings .hint.err { color: #b00; }
.doc-settings .limit-input { width: 140px; font-size: 13px; padding: 5px 8px; border: 1px solid #ccc; border-radius: 4px; }
.doc-settings .hint code { background: #eee; padding: 0 3px; border-radius: 3px; }
.doc-settings .user-input-designer { border: 1px solid #e3e3e3; border-radius: 4px; padding: 8px; background: #fff; }
.body { flex: 1; display: flex; min-height: 0; }
.editors { flex: 1 1 50%; display: flex; flex-direction: column; border-right: 1px solid #e3e3e3; min-width: 0; }
.tabs { display: flex; border-bottom: 1px solid #e3e3e3; }
.tabs button { padding: 8px 16px; border: none; background: none; font-size: 13px; cursor: pointer; }
.tabs button.active { border-bottom: 2px solid #0b5; font-weight: 600; }
.code { flex: 1; width: 100%; border: none; outline: none; resize: none; padding: 12px; font: 13px/1.5 "SFMono-Regular", Consolas, monospace; }
.page-settings { flex: 1; overflow: auto; padding: 16px; display: flex; flex-direction: column; gap: 14px; font-size: 13px; color: #333; }
.page-settings .row { display: flex; gap: 16px; flex-wrap: wrap; }
.page-settings label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #666; }
.page-settings label.block { flex: 1; }
.page-settings input, .page-settings select { font-size: 13px; padding: 5px 8px; border: 1px solid #ccc; border-radius: 4px; }
.page-settings .chrome { min-height: 90px; resize: vertical; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font: 12px/1.5 "SFMono-Regular", Consolas, monospace; }
.page-settings .hint { font-size: 11px; color: #888; margin: 0; line-height: 1.5; }
.page-settings .hint code { background: #eee; padding: 0 3px; border-radius: 3px; }
.preview { flex: 1 1 50%; display: flex; flex-direction: column; min-width: 0; overflow: hidden; background: #f6f6f6; }
/* flex-shrink:0 + z-index keep the bar from being squeezed/overlapped by the full-height preview iframe
   (otherwise the iframe covers the action buttons and swallows their clicks). */
.preview-bar { flex-shrink: 0; position: relative; z-index: 1; display: flex; align-items: center; gap: 12px; padding: 6px 12px; font-size: 12px; color: #888; }
.preview-bar .preview-actions { margin-left: auto; display: flex; align-items: center; gap: 10px; }
.preview-bar .entry-picker,
.preview-bar .listing-picker { display: flex; align-items: center; gap: 6px; }
.preview-bar .entry-picker select,
.preview-bar .listing-picker select { font-size: 12px; max-width: 200px; }
.preview-bar .muted { font-style: italic; }
.preview-bar .rerender,
.preview-bar .print,
.preview-bar .download,
.preview-bar .server-pdf { font-size: 12px; padding: 4px 12px; border: 1px solid #ccc; border-radius: 4px; background: #fff; cursor: pointer; }
.preview-bar .rerender:hover,
.preview-bar .print:hover,
.preview-bar .download:hover,
.preview-bar .server-pdf:hover:not(:disabled) { background: #f0f0f0; }
/* Server render is a debug affordance — dashed border + muted text set it apart from the primary path. */
.preview-bar .server-pdf { border-style: dashed; color: #666; }
.preview-bar .server-pdf:disabled { opacity: .6; cursor: default; }
.preview-bar .err { color: #b00; }
.preview >>> iframe { flex: 1; min-height: 0; }
</style>
